import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['printTypeSelect', 'noDarkChargeSection'];

  connect() {
    // Seeded from the server's own state so the first run is not treated as arriving at
    // DTG: a saved artwork must keep whatever selection it was rendered with.
    this.wasDTG = this.#isDigitalDTG();
    // The opt-out default fires at most once per form. Without this latch, leaving DTG and
    // coming back counts as a fresh arrival and silently re-checks a box the user had
    // deliberately un-checked - the same "charge applied without anyone choosing it" this
    // controller exists to prevent (86ak7x1kk). Seeded true when the server already
    // rendered the default, so returning to DTG never re-applies it.
    this.hasDefaulted = this.wasDTG;
    this.updatePrintType();
  }

  // The discipline controller hides the Dark section unconditionally, so re-run whenever
  // it changes: only this controller knows whether the print type offers the charge.
  produceableTypeChanged() {
    this.updatePrintType();
  }

  updatePrintType() {
    const isDTG = this.#isDigitalDTG();
    // The upcharge is opt-out for DTG, so it defaults on the first time the user arrives at
    // that print type. Only then: re-checking on every change would undo a deliberate
    // opt-out each time anything else on the form moved.
    const enteringDTG = isDTG && !this.wasDTG && !this.hasDefaulted;
    if (enteringDTG) this.hasDefaulted = true;
    this.wasDTG = isDTG;

    if (!this.hasNoDarkChargeSectionTarget) {
      // Without the target nothing is disabled and the upcharge starts leaking onto other
      // disciplines again, silently. Say so rather than no-op - but only once, since this
      // runs on every change.
      if (!this.warnedMissingSection) {
        this.warnedMissingSection = true;
        console.warn(
          '[digital-print-type] noDarkChargeSection target missing - the DK50 upcharge ' +
            'checkboxes are NOT being disabled. See 86ak7x1kk.'
        );
      }
      return;
    }

    this.noDarkChargeSectionTarget.classList.toggle('d-none', !isDTG);
    this.noDarkChargeSectionTarget
      .querySelectorAll('input[type="checkbox"]')
      .forEach((input) => {
        // `d-none` only hides: a hidden checkbox is still submitted, which is how the
        // Dark upcharge reached artwork nobody had checked it for (86ak7x1kk).
        // Disabling keeps it out of the params entirely while it is off-screen.
        input.disabled = !isDTG;
        if (enteringDTG) input.checked = true;
      });
  }

  // The select is only present while Digital is the chosen discipline, so its absence
  // means another discipline is selected and the charge is not on offer.
  #isDigitalDTG() {
    if (!this.hasPrintTypeSelectTarget) return false;

    // Keyed off the target the discipline controller actually owns, not a Bootstrap layout
    // class: `.col` would silently resolve to the wrong wrapper if the grid markup ever
    // changed, voiding the guard and restoring the bug. Absent (as on the admin form, which
    // has no discipline switcher), this falls through to the plain print-type check.
    const digitalSection = this.printTypeSelectTarget.closest(
      '[data-produceable-type-target~="digitalFields"]'
    );
    if (digitalSection?.classList.contains('d-none')) return false;

    return this.printTypeSelectTarget.value === 'DTG';
  }
}
